import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/lib/LanguageContext";

// Native HTML form POST → server-side PHP handler at vrdhaan.in.
// The PHP endpoint validates + inserts into MySQL and redirects to thankyou.php.
const SUBMIT_URL = "https://vrdhaan.in/submit.php";

export default function QuickOrder() {
    const { t } = useLang();
    const formRef = useRef(null);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const cleanPhone = (v) => {
        // Keep digits only; strip leading 91 if user typed +91XXXXXXXXXX
        let d = (v || "").replace(/\D+/g, "");
        if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
        return d;
    };

    const handleSubmit = (e) => {
        // Client-side validation mirroring the PHP rules so users don't hit a raw JSON error.
        const trimmed = name.trim();
        const p = cleanPhone(phone);
        if (trimmed.length < 3) {
            e.preventDefault();
            toast.error(t("quick.errors.name"));
            return;
        }
        if (!/^[6-9]\d{9}$/.test(p)) {
            e.preventDefault();
            toast.error(t("quick.errors.phone"));
            return;
        }
        // Normalise the phone value before native submit so PHP receives the clean 10-digit number.
        setPhone(p);
        setSubmitting(true);
        // Allow the browser to perform the native form POST and follow the redirect.
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="border border-[#3B2513] bg-[#3B2513] text-[#F4E6D0] p-6 md:p-10"
            data-testid="quick-order-card"
        >
            <div className="flex flex-col gap-6 md:gap-8">
                <div>
                    <div className="chapter-num text-[#B48F57] mb-3">
                        — {t("quick.eyebrow")}
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-[-0.01em] leading-[1.02]">
                        {t("quick.title")}
                    </h3>
                    <p className="mt-3 text-[13px] md:text-[14px] text-[#D9C9AC] leading-relaxed font-light max-w-[46ch]">
                        {t("quick.body")}
                    </p>
                </div>

                <form
                    ref={formRef}
                    action={SUBMIT_URL}
                    method="POST"
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
                    data-testid="quick-order-form"
                >
                    <div className="sm:col-span-1">
                        <label className="eyebrow text-[#D9C9AC]">
                            {t("quick.name")}
                        </label>
                        <input
                            type="text"
                            name="name"
                            className="w-full bg-white text-black placeholder:text-black/40 border border-white/20 px-4 py-3 text-[15px] font-[Manrope] focus:outline-none focus:border-[#B48F57] transition-colors"
                            placeholder={t("quick.name_ph")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            minLength={3}
                            data-testid="quick-input-name"
                        />
                    </div>
                    <div className="sm:col-span-1">
                        <label className="eyebrow text-[#D9C9AC]">
                            {t("quick.phone")}
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            className="w-full bg-white text-black placeholder:text-black/40 border border-white/20 px-4 py-3 text-[15px] font-[Manrope] focus:outline-none focus:border-[#B48F57] transition-colors"
                            placeholder={t("quick.phone_ph")}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            inputMode="numeric"
                            pattern="[0-9+\-\s]{10,14}"
                            data-testid="quick-input-phone"
                        />
                    </div>
                    <div className="sm:col-span-2 pt-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn w-full sm:w-auto bg-[#B48F57] text-[#2A1A0C] hover:bg-[#F4E6D0] hover:text-[#3B2513] disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="quick-submit"
                        >
                            {t("quick.submit")}
                            <span aria-hidden>→</span>
                        </button>
                    </div>
                </form>
            </div>
        </motion.div>
    );
}
